import {
  CoreQuery,
  DecryptBody,
  type Decrypted,
  type ICreateClientWithIdBody,
} from "@gen/app-query";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import type { ChangeEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

import { ActionDialog } from "~/components/action_dialog";
import { entries, fromEntries } from "~/utilities/object";

interface CreateClientImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (data: Partial<ICreateClientWithIdBody>) => void;
}

type FormValues = {
  [key: string]: boolean;
};

export function CreateClientImportDialog({
  isOpen,
  onClose,
  onImport,
}: CreateClientImportDialogProps) {
  const [availableFields, setAvailableFields] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileContent, setFileContent] = useState<Record<
    string,
    unknown
  > | null>(null);

  const {
    control,
    reset,
    handleSubmit,
    getValues,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {},
    shouldUnregister: false,
  });

  const parseClient = (clientStringified: string) => {
    try {
      const clientParsed = JSON.parse(clientStringified);

      if (!clientParsed || typeof clientParsed !== "object") {
        resetFileUpload();
        return;
      }

      const fields = Object.keys(clientParsed);

      if (fields.length === 0) {
        resetFileUpload();
        return;
      }

      loadClient(clientParsed, fields);
    } catch (err) {
      console.error("Failed to parse client:", err);
      resetFileUpload();
    }
  };

  const { mutate: decrypt, isPending: isDecrypting } =
    CoreQuery.useAdmin_decryptMutation({
      onSuccess(resp: Decrypted) {
        parseClient(resp.plaintext);
      },
      onError(error) {
        console.error("Decryption failed:", error);
      },
    });

  const handleFilePick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const text = (await file.text()).trim();

    try {
      if (text.startsWith("{")) {
        parseClient(text);
      } else {
        decrypt(DecryptBody.fromJS({ ciphertext: text }));
      }
    } catch (err) {
      console.error("Failed to read configuration:", err);
    }
  };

  const loadClient = (client: Record<string, unknown>, fields: string[]) => {
    setFileContent(client);
    setAvailableFields(fields);

    const defaults: FormValues = {};
    fields.forEach((field) => {
      defaults[field] = true;
    });

    reset(defaults);
  };

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    if (!fileContent) {
      return;
    }
    const payload = fromEntries(
      entries(values)
        .filter(([_, v]) => v)
        .map(([k]) => {
          return [k, fileContent![k]];
        }),
    ) as Partial<ICreateClientWithIdBody>;
    onImport(payload);
    onClose();
  };

  const resetFileUpload = useCallback(() => {
    setFileContent(null);
    setAvailableFields([]);
    reset({});
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [reset]);

  useEffect(() => {
    if (!isOpen) {
      resetFileUpload();
    }
  }, [isOpen, resetFileUpload]);

  if (isOpen) {
    return (
      <ActionDialog
        dataTestId="import-client"
        heading="Import Client Configuration"
        confirm={handleSubmit(onSubmit)}
        cancel={onClose}
        cancelButtonMessage="Close"
        confirmButtonMessage="Import"
        isConfirming={isSubmitting}
        isDisabled={
          !fileContent ||
          availableFields.length === 0 ||
          !Object.values(getValues()).some(Boolean)
        }
      >
        <Stack spacing={1}>
          {!fileContent ? (
            <Stack spacing={2} alignItems="flex-start">
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileOutlinedIcon />}
                loading={isDecrypting}
                data-testid="choose-file-button"
              >
                Choose File
                <input
                  hidden
                  type="file"
                  accept=".json,.enc,text/plain,application/json"
                  ref={(el) => {
                    fileInputRef.current = el;
                  }}
                  onChange={(e) => {
                    handleFilePick(e);
                  }}
                />
              </Button>
            </Stack>
          ) : (
            <Stack spacing={1}>
              {availableFields.map((f) => (
                <Controller
                  key={f}
                  name={f}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          slotProps={{
                            input: {
                              "data-testid": `import-${f}-checkbox`,
                            },
                          }}
                        />
                      }
                      label={f}
                    />
                  )}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </ActionDialog>
    );
  }

  return null;
}
